import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper, useUnsavedChanges } from "aic";
import { CommentReadDto } from "functions/api/comments/interface";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { getLibraries } from "functions/api/library/getLibraries";
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useReducer,
  useRef,
  useState,
  useTransition,
} from "react";
import { useLoaderData } from "react-router-dom";
import { Patch } from "rfc6902";
import { eHierarchy } from "../functions/api/enums";
import { HierarchyLoaderData } from "../functions/api/hierarchy/hierarchyLoader";
import { restoreBackupReport } from "../functions/api/hierarchy/restoreBackupReport";
import {
  HierarchySaveDto,
  HierarchyUpdateDto,
  saveHierarchyData,
} from "../functions/api/hierarchy/saveHierarchyData";
import {
  SaveTocAsMajorVersionParams,
  saveTocAsMajorVersion,
} from "../functions/api/hierarchy/saveTocAsMajorVersion";
import { IRequestStatus } from "../functions/api/interface";
import { useDocumentTitle } from "../functions/useDocumentTitle";
import { AppSettingsContext } from "./AppSettingsContext";
import { getHierarchyBaseUrl } from "./hierarchy/getHierarchyBaseUrl";
import { hierarchyContextReducer } from "./hierarchy/hierarchyContextReducer";
import { hierarchyInitialState } from "./hierarchy/hierarchyInitialState";
import {
  LOAD_CLINICAL_LIBRARY,
  LOAD_HIERARCHY,
  PATCH_HIERARCHY_COMMENTS,
  REMOVE_HIERARCHY_COMMENT,
  SAVE_HIERARCHY_COMPLETED,
  SAVE_HIERARCHY_FAILED,
  SET_CLINICAL_LIBRARY_LOADING,
  UPDATE_HIERARCHY_COMMENT,
} from "./hierarchy/interface";
import { HierarchyContext } from "./HierarchyContext";
import { HubGroupMessage } from "./signalr/interface";
import { SignalRContext } from "./SignalRProvider";

interface HierarchyContextProviderProps {
  children: React.ReactNode;
}

/**
 * Provides hierarchy context and management functionality to child components.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 */
export const HierarchyContextProvider = ({ children }: HierarchyContextProviderProps) => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyLoaderData = useLoaderData() as HierarchyLoaderData;
  const signalR = useContext(SignalRContext);
  const { navigateAfterSave, registerUnsavedChanges } = useUnsavedChanges(
    "hierarchy-context-provider",
  );
  const [state, dispatch] = useReducer(hierarchyContextReducer, hierarchyInitialState);
  const [saveRequestStatus, setSaveRequestStatus] = useState<IRequestStatus<HierarchySaveDto>>({
    requesting: false,
    error: false,
  });
  const [saveTocRequestStatus, setSaveTocRequestStatus] = useState<
    IRequestStatus<SaveTocAsMajorVersionParams>
  >({
    requesting: false,
    error: false,
  });
  const [clinicalLibrariesLoadStatus, setClinicalLibrariesLoadStatus] = useState<
    IRequestStatus<string[]>
  >({
    requesting: false,
    error: false,
  });
  const [, startTransition] = useTransition();

  const currentBaseUrlRef = useRef<string>("");

  /**
   * Add loader data into context
   * Check if the loader data is valid and if the hierarchy has changed
   */
  useEffect(() => {
    if (
      hierarchyLoaderData &&
      hierarchyLoaderData.hierarchy &&
      hierarchyLoaderData.accessRights &&
      hierarchyLoaderData.parentHierarchies &&
      // Reload if the Hierarchy is changed even if the name change is not saved
      (!state.isNameChanged ||
        !state.hierarchy ||
        state.hierarchy.id !== hierarchyLoaderData.hierarchy.id) &&
      (!state.hierarchy ||
        getHierarchyBaseUrl(state.hierarchy) !== getHierarchyBaseUrl(hierarchyLoaderData.hierarchy))
    ) {
      dispatch({
        operation: LOAD_HIERARCHY,
        hierarchy: hierarchyLoaderData.hierarchy,
        hierarchyNodes: hierarchyLoaderData.parentHierarchies,
        accessRights: hierarchyLoaderData.accessRights,
        isSubscribed: hierarchyLoaderData.isHierarchySubscribed,
      });
      currentBaseUrlRef.current = getHierarchyBaseUrl(hierarchyLoaderData.hierarchy);
    }
  }, [hierarchyLoaderData, state.hierarchy, state.id, state.isNameChanged]);

  /**
   * Memoised parent hierarchy nodes filtered by hierarchyLinks.
   * Only includes hierarchy nodes that are parents of the current hierarchy
   * based on the hierarchyLinks array.
   */
  const parentNodes = useMemo(() => {
    const parentHierarchyIds =
      state.hierarchy?.hierarchyLinks?.map((link) => link.hierarchyId) ?? [];
    return state.hierarchyNodes.filter((node) => parentHierarchyIds.includes(node.id));
  }, [state.hierarchy?.hierarchyLinks, state.hierarchyNodes]);

  /**
   * Send a SignalR update to the group when the hierarchyId is changed because of a save major version
   * @param oldHierarchyId Old hierarchy ID
   * @param newHierarchy New hierarchy data
   */
  const sendSignalRHierarchyUpdate = useCallback(
    async (oldHierarchyId: string, newHierarchy: HierarchyReadDto) => {
      if (signalR && signalR.connection?.state === HubConnectionState.Connected) {
        signalR.sendTocSaveToGroup({ oldHierarchyId, newHierarchy }, `toc${oldHierarchyId}`);
      }
    },
    [signalR],
  );

  /**
   * Lazy load clinical libraries for the current hierarchy and parent hierarchies.
   * This callback runs after the hierarchy is loaded and loads clinical libraries separately.
   * It collects all hierarchy IDs (current + parents), fetches their clinical libraries,
   * and dispatches them to the context state. If the load fails or returns no data,
   * it sets the loading flag to false.
   *
   */
  const loadClinicalLibraries = useCallback(async () => {
    const currentHierarchyIdAndNodes = Array.from(
      new Set([state.id, ...parentNodes.map((ph) => ph.id)]),
    );
    const response = await loadWrapper(
      getLibraries,
      currentHierarchyIdAndNodes,
      clinicalLibrariesLoadStatus,
      setClinicalLibrariesLoadStatus,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (response?.success) {
      dispatch({
        operation: LOAD_CLINICAL_LIBRARY,
        clinicalLibrary: response.data,
      });
    } else if (response?.success === false) {
      dispatch({
        operation: SET_CLINICAL_LIBRARY_LOADING,
        isClinicalLibraryLoading: false,
      });
    }
  }, [appSettings.backEndUrl, clinicalLibrariesLoadStatus, parentNodes, state.id]);

  /**
   * Effect to trigger clinical library loading when needed.
   * Runs when the hierarchy or parent hierarchy nodes change and checks if any of them
   * have the hasClinicalLibrary flag set but don't have the clinicalLibrary data loaded.
   * Sets the loading state and calls loadClinicalLibraries to fetch the data.
   */
  useEffect(() => {
    if (
      !state.isClinicalLibraryLoading &&
      !clinicalLibrariesLoadStatus.requesting &&
      !clinicalLibrariesLoadStatus.error &&
      ((state.hierarchy &&
        state.hierarchy.hasClinicalLibrary &&
        !state.hierarchy.clinicalLibrary) ||
        parentNodes.some((ph) => ph.hasClinicalLibrary && !ph.clinicalLibrary))
    ) {
      dispatch({
        operation: SET_CLINICAL_LIBRARY_LOADING,
        isClinicalLibraryLoading: true,
      });
      loadClinicalLibraries();
    }
  }, [
    clinicalLibrariesLoadStatus.error,
    clinicalLibrariesLoadStatus.requesting,
    loadClinicalLibraries,
    parentNodes,
    state.hierarchy,
    state.isClinicalLibraryLoading,
  ]);

  /**
   * Save the current hierarchy state
   * Does not send a SignalR update
   */
  const saveHierarchy = useEffectEvent(async () => {
    /* istanbul ignore else */
    if (state.hierarchy) {
      const updateDto: HierarchyUpdateDto = {
        id: state.hierarchy.id,
        name: state.hierarchy.name,
        parentTag: state.hierarchy.parentTag,
        description: state.hierarchy.description,
        lists: state.hierarchy.lists,
        phase: state.hierarchy.phase,
        reportType: state.hierarchy.reportType,
        rowGroups: state.hierarchy.rowGroups,
        sections: state.hierarchy.sections,
        singles: state.hierarchy.singles,
        status: state.hierarchy.status,
        domainList: state.hierarchy.domainList,
        domainReplacements: state.hierarchy.domainReplacements,
        arsEnabled: state.hierarchy.arsEnabled,
        analysisSets: state.hierarchy.analysisSets,
        dataSubsets: state.hierarchy.dataSubsets,
        groupingFactors: state.hierarchy.groupingFactors,
      };
      const response =
        state.hierarchy.isBackupVersion && state.hierarchy.type === eHierarchy.Report
          ? await loadWrapper(
              restoreBackupReport,
              updateDto,
              { ...saveRequestStatus, error: false, requestedId: undefined, errorText: undefined },
              setSaveRequestStatus,
              appSettings.backEndUrl,
            )
          : await loadWrapper(
              saveHierarchyData,
              updateDto,
              {
                ...saveRequestStatus,
                error: false,
                requestedId: undefined,
                errorText: undefined,
              },
              setSaveRequestStatus,
              appSettings.backEndUrl,
            );
      /* istanbul ignore else */
      if (response?.success) {
        dispatch({ operation: SAVE_HIERARCHY_COMPLETED, hierarchy: response.data });
        setClinicalLibrariesLoadStatus({ requesting: false, error: false });
        const newBaseUrl = getHierarchyBaseUrl(response.data);
        // When restoring a back up, this should trigger
        if (state.hierarchy.originHierarchyId)
          await sendSignalRHierarchyUpdate(state.hierarchy.originHierarchyId, response.data);
        if (response.data.id !== state.hierarchy.id || newBaseUrl !== currentBaseUrlRef.current) {
          currentBaseUrlRef.current = newBaseUrl;
          navigateAfterSave(newBaseUrl);
        }
      } else if (response?.success === false) {
        console.warn(`Save failed for hierarchy: ${saveRequestStatus.errorText}`);
        dispatch({ operation: SAVE_HIERARCHY_FAILED });
      }
    }
  });

  useEffect(() => {
    if (state.doSave && !saveRequestStatus.requesting) {
      saveHierarchy();
    }
  }, [saveRequestStatus.requesting, state.doSave]);

  /**
   * Save the current hierarchy as a new major version
   * This will send a SignalR update to the group to indicate that the hierarchyId has changed
   * @param saveTocDescription Description for the save
   */
  const saveTocMajor = useEffectEvent(async (saveTocDescription: string) => {
    /* istanbul ignore else */
    if (state.hierarchy) {
      const updateDto: HierarchyUpdateDto = {
        id: state.hierarchy.id,
        name: state.hierarchy.name,
        parentTag: state.hierarchy.parentTag,
        description: state.hierarchy.description,
        lists: state.hierarchy.lists,
        phase: state.hierarchy.phase,
        reportType: state.hierarchy.reportType,
        rowGroups: state.hierarchy.rowGroups,
        sections: state.hierarchy.sections,
        singles: state.hierarchy.singles,
        status: state.hierarchy.status,
        domainList: state.hierarchy.domainList,
        domainReplacements: state.hierarchy.domainReplacements,
        arsEnabled: state.hierarchy.arsEnabled,
        analysisSets: state.hierarchy.analysisSets,
        dataSubsets: state.hierarchy.dataSubsets,
        groupingFactors: state.hierarchy.groupingFactors,
      };

      const response = await loadWrapper(
        saveTocAsMajorVersion,
        {
          hierarchy: updateDto,
          hierarchyId: updateDto.id,
          parentTag: updateDto.parentTag,
          saveTocDescription,
        },
        {
          ...saveTocRequestStatus,
          error: false,
          requestedId: undefined,
          errorText: undefined,
        },
        setSaveTocRequestStatus,
        appSettings.backEndUrl,
      );
      /* istanbul ignore else */
      if (response?.success) {
        setClinicalLibrariesLoadStatus({ requesting: false, error: false });
        dispatch({
          operation: SAVE_HIERARCHY_COMPLETED,
          hierarchy: response.data,
          oldHierarchyId: state.hierarchy.id,
        });
        // Always send signalR update
        await sendSignalRHierarchyUpdate(state.hierarchy.id, response.data);
        /* istanbul ignore else */
        if (response.data.id !== state.hierarchy.id) {
          const newBaseUrl = getHierarchyBaseUrl(response.data);
          currentBaseUrlRef.current = newBaseUrl;
          navigateAfterSave(newBaseUrl);
        }
      } else if (response?.success === false) {
        console.warn(`Save failed for hierarchy: ${saveRequestStatus.errorText}`);
        dispatch({ operation: SAVE_HIERARCHY_FAILED });
      }
    }
  });

  /**
   * Effect to save the ToC as a major version when the state.doTocSave is true
   */
  useEffect(() => {
    if (state.doTocSave && !saveTocRequestStatus.requesting && state.saveTocDescription) {
      saveTocMajor(state.saveTocDescription);
    }
  }, [saveTocRequestStatus.requesting, state.doTocSave, state.saveTocDescription]);

  /**
   * Join signalR group for the hierarchy
   * This is done to receive comment updates for the hierarchy
   */
  const [sendGroupRequest, setSendGroupRequest] = useState<string>("❄️");
  useEffect(() => {
    if (
      signalR &&
      signalR.connection?.state === HubConnectionState.Connected &&
      state.hierarchy &&
      sendGroupRequest !== `hie${state.hierarchy.id}`
    ) {
      signalR.connectToGroup(`hie${state.hierarchy.id}`);
      startTransition(() => {
        setSendGroupRequest(`hie${state.hierarchy!.id}`);
      });
    }
  }, [sendGroupRequest, signalR, signalR?.connection?.state, state.hierarchy]);

  /**
   * Create hierarchy level signalR group listener
   */
  useEffect(() => {
    /**
     * Handle incoming SignalR group messages for hierarchy updates.
     *
     * @param message - Raw JSON string containing the group message payload.
     */
    const processSignalRMessage = (message: string) => {
      const hieUpdate = JSON.parse(message) as HubGroupMessage;
      if (
        hieUpdate.groupName === `hie${state.hierarchy?.id}` &&
        hieUpdate.displayName !== signalR?.connection?.connectionId &&
        hieUpdate.displayName !== "Backend" &&
        hieUpdate.text &&
        state.hierarchy?.comments
      ) {
        switch (hieUpdate.messageType) {
          case "deleteComment": {
            try {
              const hieDelCommentId = JSON.parse(hieUpdate.text[0]).id;
              /* istanbul ignore else */
              if (hieDelCommentId) {
                dispatch({
                  operation: REMOVE_HIERARCHY_COMMENT,
                  commentId: hieDelCommentId,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing delete comment update:", error);
            }
            break;
          }
          case "comment": {
            try {
              const hieUpdateMessage = JSON.parse(hieUpdate.text[0]) as CommentReadDto;
              /* istanbul ignore else */
              if (hieUpdateMessage) {
                dispatch({
                  operation: UPDATE_HIERARCHY_COMMENT,
                  comment: hieUpdateMessage,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comment update:", error);
            }
            break;
          }
          case "commentsPatch": {
            try {
              const commentsPatch = JSON.parse(hieUpdate.text[0]) as Patch;
              /* istanbul ignore else */
              if (commentsPatch) {
                dispatch({
                  operation: PATCH_HIERARCHY_COMMENTS,
                  patch: commentsPatch,
                });
              }
            } catch (error) {
              // istanbul ignore next
              console.warn("Error processing comments patch update:", error);
            }
            break;
          }
        }
      }
    };
    signalR?.connection?.on("SendMessageToGroup", processSignalRMessage);
    // return () => {
    //   signalR?.connection?.off("SendMessageToGroup", processSignalRMessage);
    // };
  }, [signalR?.connection, state.hierarchy?.comments, state.hierarchy?.id]);

  /**
   * Page title based on hierarchy state
   */
  const title = state.hierarchy ? `${state.hierarchy.name}` : `Loading hierarchy... ${state.id}`;
  useDocumentTitle(title);

  /**
   * Effect to register unsaved changes when the hierarchy state changes
   */
  useEffect(() => {
    registerUnsavedChanges("Hierarchy", state.changed);
  }, [registerUnsavedChanges, state.changed]);

  return (
    <HierarchyContext
      value={{
        ...state,
        dispatch,
        title,
        saveRequestStatus,
        saveTocRequestStatus,
      }}
    >
      <div
        id="hierarchyContextProvider"
        style={{ position: "relative", width: "100%", height: "100%" }}
      >
        {children}
      </div>
    </HierarchyContext>
  );
};

HierarchyContextProvider.displayName = "HierarchyContextProvider";
